<template>
  <div ref="racine" class="relative">
    <button
      type="button"
      class="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-100 dark:hover:bg-alt-dark-raised"
      :aria-expanded="ouvert"
      aria-haspopup="menu"
      aria-label="Mon profil"
      @click.stop="ouvert = !ouvert"
    >
      <AltAvatar :name="auth.user?.displayName ?? ''" size="sm" />
      <span class="hidden max-w-[12rem] truncate text-sm text-slate-600 dark:text-alt-dark-muted sm:inline">
        {{ auth.user?.displayName }}
      </span>
      <span aria-hidden="true" class="text-xs text-slate-400">▾</span>
    </button>

    <div
      v-if="ouvert"
      role="menu"
      class="absolute right-0 z-50 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-xl border border-slate-200 bg-white shadow-lg dark:border-alt-dark-border dark:bg-alt-dark-surface"
      @keydown.esc="ouvert = false"
    >
      <!-- Identité + rôles -->
      <div class="border-b border-slate-100 p-4 dark:border-alt-dark-border">
        <p class="truncate font-medium text-space-indigo dark:text-altoneo-50">{{ auth.user?.displayName }}</p>
        <p class="truncate text-xs text-slate-500 dark:text-alt-dark-muted">{{ auth.user?.email }}</p>
        <div class="mt-1.5 flex flex-wrap gap-1">
          <AltBadge v-for="r in auth.roles" :key="r" variant="neutral" size="sm">{{ ROLE_LABELS[r] }}</AltBadge>
        </div>
        <p v-if="auth.user?.matricule || auth.user?.societe" class="mt-1.5 text-xs text-slate-500 dark:text-alt-dark-muted">
          {{ [auth.user?.matricule, auth.user?.societe, auth.user?.site].filter(Boolean).join(' · ') }}
        </p>
      </div>

      <!--
        L'ADRESSE DE DÉPART PAR DÉFAUT. Elle pré-remplit chaque nouvelle
        déclaration : la corriger ici évite de la retaper à chaque déplacement.
      -->
      <form class="border-b border-slate-100 p-3 dark:border-alt-dark-border" @submit.prevent="enregistrerAdresse">
        <AltFormField
          v-slot="{ id }"
          label="Adresse de départ par défaut"
          hint="Proposée comme adresse de départ de chaque nouvelle déclaration."
          :error="erreurAdresse"
        >
          <ChampAdresse :id="id" v-model="adresse" compact />
        </AltFormField>
        <div class="mt-2 flex justify-end">
          <AltButton type="submit" size="sm" variant="secondary" :loading="enregistrement" :disabled="!adresseModifiee">
            Enregistrer
          </AltButton>
        </div>
      </form>

      <!-- Thème -->
      <div class="border-b border-slate-100 p-3 dark:border-alt-dark-border">
        <p class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Thème</p>
        <div class="flex gap-1" role="radiogroup" aria-label="Thème">
          <button
            v-for="opt in optionsTheme"
            :key="opt.value"
            type="button"
            role="radio"
            :aria-checked="themePreference === opt.value"
            class="flex-1 rounded-md border px-2 py-1 text-xs transition-colors"
            :class="
              themePreference === opt.value
                ? 'border-space-indigo bg-space-indigo text-white dark:border-alt-dark-link dark:bg-alt-dark-raised'
                : 'border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-alt-dark-border dark:text-alt-dark-muted dark:hover:bg-alt-dark-raised'
            "
            @click="setTheme(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <button
        type="button"
        role="menuitem"
        class="flex w-full items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-left text-sm text-space-indigo hover:bg-slate-50 dark:border-alt-dark-border dark:text-altoneo-50 dark:hover:bg-alt-dark-raised"
        @click="decouvrir"
      >
        <span aria-hidden="true">🧭</span> Visite guidée &amp; mode démo
      </button>

      <!-- En démo, « se déconnecter » n'a pas de sens : on quitte la démo. -->
      <button
        type="button"
        role="menuitem"
        class="flex w-full items-center gap-2 rounded-b-xl px-4 py-2.5 text-left text-sm text-amaranth hover:bg-slate-50 dark:hover:bg-alt-dark-raised"
        @click="deconnexion"
      >
        <span aria-hidden="true">⏻</span> {{ decouverte.actif ? 'Quitter la démo' : 'Déconnexion' }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ROLE_LABELS } from '@igd/shared';
import { useAuthStore } from '@/stores/auth';
import { useContexteStore } from '@/stores/contexte';
import { setTheme, themePreference, type ThemePreference } from '@/lib/preferences';
import { erreursParChamp, messageErreur } from '@/lib/api';
import { useNotifier } from '@/composables/useNotifier';
import { useModeDemo } from '@/composables/useModeDemo';
import { useDecouverteStore } from '@/stores/decouverte';
import ChampAdresse from '@/components/ChampAdresse.vue';

const auth = useAuthStore();
const contexte = useContexteStore();
const router = useRouter();
const notifier = useNotifier();
const decouverte = useDecouverteStore();
const modeDemo = useModeDemo();

const ouvert = ref(false);
const racine = ref<HTMLElement | null>(null);

const optionsTheme: Array<{ value: ThemePreference; label: string }> = [
  { value: 'clair', label: 'Clair' },
  { value: 'sombre', label: 'Sombre' },
  { value: 'systeme', label: 'Système' },
];

const adresse = ref(auth.user?.adresseDomicile ?? '');
const erreurAdresse = ref('');
const enregistrement = ref(false);
const adresseModifiee = computed(() => adresse.value.trim() !== (auth.user?.adresseDomicile ?? ''));

// À chaque ouverture, on repart de la valeur enregistrée : une saisie
// abandonnée ne doit pas réapparaître comme si elle avait été gardée.
watch(ouvert, (o) => {
  if (o) {
    adresse.value = auth.user?.adresseDomicile ?? '';
    erreurAdresse.value = '';
  }
});

async function enregistrerAdresse(): Promise<void> {
  erreurAdresse.value = '';
  enregistrement.value = true;
  try {
    await auth.majAdresseDomicile(adresse.value.trim() || null);
    notifier.succes('Adresse de départ enregistrée.');
  } catch (e) {
    erreurAdresse.value = erreursParChamp(e).adresseDomicile ?? messageErreur(e);
  } finally {
    enregistrement.value = false;
  }
}

function decouvrir(): void {
  ouvert.value = false;
  void router.push('/decouverte');
}

async function deconnexion(): Promise<void> {
  ouvert.value = false;
  if (decouverte.actif) {
    await modeDemo.quitter();
    return;
  }
  await auth.logout();
  contexte.vider();
  await router.replace({ name: 'login' });
}

// Un clic ailleurs referme le menu, comme tout menu déroulant.
function clicDehors(e: MouseEvent): void {
  if (ouvert.value && racine.value && !racine.value.contains(e.target as Node)) ouvert.value = false;
}
onMounted(() => document.addEventListener('click', clicDehors));
onBeforeUnmount(() => document.removeEventListener('click', clicDehors));
</script>
